import React from 'react';
import { useTranslation } from '@shared/core';
import { useAddressFormController } from './AddressForm.controller';
import { StyledButtonsContainer, StyledGroupRow, StyledStack, StyledAddressFormContent, StyledRequiredFieldText, StyledDialogV2, styles } from './AddressForm.styles';
import { ButtonV2, FormControl, InputV2, SelectV1, Spinner, TextV2, CancelButton, Divider, SpacingStack } from '@withjoy/joykit';
import type { ShippingAddressFragment } from '@graphql/aliases';
import { AddressAutocompleteField } from '@shared/components/AddressAutocompleteField';
import { DelayShippingSettings } from './DelayShippingSettings';
import { useGetEventDelayShipSettingQuery } from '../../../../../admin/Registry.graphql.generated';
import { useAdminRegistryState } from '@apps/registry/admin/state/context/AdminRegistry';
import { ShippingControlMessage } from './ShippingControlMessage';

interface AddressFormProps
  extends Readonly<{
    registryShippingAddress: ShippingAddressFragment | null;
  }> {}

export const AddressForm: React.FC<AddressFormProps> = ({ registryShippingAddress }) => {
  const { t } = useTranslation('yourRegistry');
  const {
    address1,
    poBoxWarningCaption,
    shippingSettingsTitle,
    note,
    shippingPolicyExceptions,
    name,
    nameCaption,
    phone,
    address2,
    city,
    state,
    postalCode,
    country,
    saveButton,
    cancelButton,
    resetButton,
    countryOtherByCode,
    requiredFields,
    fullName,
    phonePlaceholder,
    address,
    addressPlaceholder2,
  } = t('addressDialog');

  const {
    formik,
    isCountryLoading,
    isOtherCountryFieldVisible,
    countryList,
    countryDropdownValue,
    otherCountryFieldValue,
    handleCountryDropdownChange,
    handleOtherCountryFieldChange,
    handlePostalCodeFieldChange,
    handleNextStep,
    handleClearAddress,
    address2Error,
    stateError,
    cityError,
    postalCodeError,
    countryError,
    nameError,
    phoneError,
    loading,
    onClose,
    isNationalAddress,
    clearAddressPressed,
    setSuggestedAddress,
    onCallSuggestionApi,
    setAddressUsed,
    onDisableAddressSuggestions,
  } = useAddressFormController({
    registryShippingAddress,
    countryOtherByCode,
  });
  const buttonWidth = ['100%', '132px'];
  const { eventId } = useAdminRegistryState();
  const { data: registryData } = useGetEventDelayShipSettingQuery({
    batchMode: 'fast',
    variables: { eventId },
  });
  const hideDelayShipment = registryData?.getRegistryEventData?.hideDelayShipSetting ?? true;

  return (
    <StyledAddressFormContent __css={styles.addressFormContent}>
      <StyledDialogV2.Header>
        <TextV2 typographyVariant="hed2">{shippingSettingsTitle()}</TextV2>
        <StyledDialogV2.CloseButton onClick={onClose} />
      </StyledDialogV2.Header>

      <StyledDialogV2.Body __css={styles.dialogV2Body}>
        {!hideDelayShipment ? <DelayShippingSettings /> : <Divider />}
        <form>
          <StyledStack
            padding={['20px 24px', '24px 32px', '24px 32px']}
            spacing={6}
            __css={styles.stack}
          >
            {!hideDelayShipment ? (
              <>
                <TextV2 typographyVariant="body1">{note()}</TextV2>
                <TextV2 typographyVariant="body1">{shippingPolicyExceptions()}</TextV2>
              </>
            ) : (
              <ShippingControlMessage />
            )}
            <FormControl
              label={`${name()}${'\u2009*'}`}
              caption={
                <TextV2
                  typographyVariant="label2"
                  color="mono10"
                >
                  {nameCaption()}
                </TextV2>
              }
              isInvalid={!!nameError}
              error={nameError}
              isRequired
            >
              <InputV2
                {...formik.getFieldProps('name')}
                placeholder={fullName()}
              />
            </FormControl>
            <FormControl
              label={phone()}
              isInvalid={!!phoneError}
              error={phoneError}
            >
              <InputV2
                {...formik.getFieldProps('phone')}
                placeholder={phonePlaceholder()}
              />
            </FormControl>
            {!isCountryLoading && (
              <FormControl
                isDisabled={isCountryLoading}
                label={country()}
                isInvalid={!!countryError}
                error={countryError}
              >
                <SpacingStack spacing={3}>
                  <SelectV1
                    options={countryList}
                    value={countryDropdownValue}
                    searchable={false}
                    onChange={handleCountryDropdownChange}
                  />
                  {isOtherCountryFieldVisible && (
                    <InputV2
                      {...formik.getFieldProps('country')}
                      value={otherCountryFieldValue}
                      required
                      onChange={handleOtherCountryFieldChange}
                    />
                  )}
                </SpacingStack>
              </FormControl>
            )}
            <AddressAutocompleteField
              label={`${address1()}${'\u2009*'}`}
              caption={poBoxWarningCaption()}
              placeholderError={poBoxWarningCaption()}
              placeholder={address()}
              initialAddress1={registryShippingAddress?.address1 ?? ''}
              formik={formik}
              formikKey="address1"
              isNationalAddress={isNationalAddress}
              countryDropdownValue={countryDropdownValue.value}
              clearAddressPressed={clearAddressPressed}
              setSuggestedAddress={setSuggestedAddress}
              setAddressUsed={setAddressUsed}
              onCallSuggestionApi={onCallSuggestionApi}
              onDisableAddressSuggestions={onDisableAddressSuggestions}
            />
            <FormControl
              label={address2()}
              isInvalid={!!address2Error}
              error={address2Error}
              marginTop={[32, 32, 0]}
            >
              <InputV2
                {...formik.getFieldProps('address2')}
                autoComplete="none"
                placeholder={addressPlaceholder2()}
              />
            </FormControl>
            <StyledGroupRow __css={styles.groupRow}>
              <FormControl
                label={`${city()}${'\u2009*'}`}
                isInvalid={!!cityError}
                error={cityError}
                isRequired
              >
                <InputV2
                  {...formik.getFieldProps('city')}
                  placeholder={city()}
                />
              </FormControl>
              <FormControl
                label={`${state()}${'\u2009*'}`}
                isInvalid={!!stateError}
                error={stateError}
                isRequired
                marginTop={[32, 32, 0]}
              >
                <InputV2
                  {...formik.getFieldProps('state')}
                  placeholder={state()}
                />
              </FormControl>
            </StyledGroupRow>
            <FormControl
              label={`${postalCode()}${'\u2009*'}`}
              isInvalid={!!postalCodeError}
              error={postalCodeError}
              isRequired
              marginTop={[32, 32, 0]}
            >
              <InputV2
                {...formik.getFieldProps('postalCode')}
                onChange={handlePostalCodeFieldChange}
                placeholder={postalCode()}
              />
            </FormControl>
            <StyledRequiredFieldText
              typographyVariant="label5"
              __css={styles.requiredFieldText}
            >
              {requiredFields()}
            </StyledRequiredFieldText>
          </StyledStack>
        </form>
      </StyledDialogV2.Body>
      <Divider />
      <StyledDialogV2.Footer borderRadius={[null, '0 0 12px 12px', '0 0 12px 12px']}>
        <StyledButtonsContainer
          paddingX={['24px', '32px', '32px']}
          __css={styles.buttonsContainer}
        >
          <ButtonV2
            intent="destructive"
            marginRight="auto"
            variant="ghost"
            onClick={handleClearAddress}
            width={buttonWidth}
          >
            {resetButton()}
          </ButtonV2>
          <CancelButton
            disabled={loading}
            onClick={onClose}
            width={buttonWidth}
          >
            {cancelButton()}
          </CancelButton>
          <ButtonV2
            intent="primary"
            onClick={handleNextStep}
            disabled={loading || !formik.isValid}
            type="submit"
            width={buttonWidth}
          >
            {loading ? <Spinner /> : saveButton()}
          </ButtonV2>
        </StyledButtonsContainer>
      </StyledDialogV2.Footer>
    </StyledAddressFormContent>
  );
};
